/** @jsxImportSource ./simboluri-jsx */
// Host wrapper for ported structure pages.
//
// Two jobs:
//
//   * carries the `obs` class every ported page's CSS is scoped under
//     (legacy.css prefixes each old selector with `.obs `);
//   * turns the pages' plain `<a href="/p/...">` links into SPA navigations.
//     The old app was hash-routed, so its links never reloaded; the ported
//     pages still render hundreds of plain anchors (via legaturi.ts, which
//     now emits react-router PATHS). Rewriting each one to <Link> would be
//     churn across ~50 files — one delegated click handler does the same.
//     Anchors that leave the app (downloads, external, target=_blank) and
//     modified clicks (new tab) keep the browser behavior.

import { useCallback, type MouseEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

export default function ObsHost({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const navigate = useNavigate();
  const onClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement).closest("a");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || !href.startsWith("/")) return;
      if (a.target && a.target !== "_self") return;
      if (a.hasAttribute("download")) return;
      // API routes (CSV exports, drawings) are documents, not screens
      if (href.startsWith("/api/")) return;
      e.preventDefault();
      navigate(href);
    },
    [navigate]
  );
  return (
    <div className={`obs ${className}`.trim()} onClick={onClick}>
      {children}
    </div>
  );
}
